import { useMemo } from 'react';
import { OTHERS_COLOR } from '../../utils/coalitionLookup';

// A simple alluvial-style diagram. For each year column, render a stacked vertical
// bar where each segment is a coalition sized by seats. Connect coalition N in year Y
// to its strongest party-overlap successor in year Y+1 with a translucent ribbon.
const CoalitionLineage = ({ history }) => {
  const W = 960;
  const H = 360;
  const PAD_X = 48;
  const PAD_Y = 24;
  const COL_W = (W - PAD_X * 2) / (history.length - 1);

  const columns = useMemo(() => {
    return history.map((election, idx) => {
      const cx = PAD_X + idx * COL_W;
      const total = election.totalSeats || Object.values(election.nationalTotals || {}).reduce((a, b) => a + b, 0);
      const innerH = H - PAD_Y * 2;
      const groups = Object.entries(election.coalitions || {}).map(([name, parties]) => {
        let seats = 0;
        (election.states || []).forEach((s) => {
          Object.entries(s.breakdown || {}).forEach(([party, n]) => {
            if (parties.includes(party)) seats += n;
          });
        });
        if (seats === 0 && election.nationalTotals?.[name]) seats = election.nationalTotals[name];
        return { name, parties, seats };
      }).filter((g) => g.seats > 0).sort((a, b) => b.seats - a.seats);

      let y = PAD_Y;
      const segments = groups.map((g) => {
        const h = (g.seats / total) * innerH;
        const raw = election.coalitionColors?.[g.name];
        const color = !raw || raw.toLowerCase() === '#f5f5f5' ? OTHERS_COLOR : raw;
        const seg = { ...g, y, h, color };
        y += h + 2;
        return seg;
      });
      return { year: election.year, x: cx, segments };
    });
  }, [history, COL_W]);

  // Build ribbons between consecutive years.
  const ribbons = [];
  for (let i = 0; i < columns.length - 1; i++) {
    const left = columns[i];
    const right = columns[i + 1];
    left.segments.forEach((lSeg) => {
      const electionLeft = history[i];
      const partiesLeft = electionLeft.coalitions[lSeg.name] || [];
      let best = null;
      let bestOverlap = 0;
      right.segments.forEach((rSeg) => {
        const electionRight = history[i + 1];
        const partiesRight = electionRight.coalitions[rSeg.name] || [];
        const overlap = partiesLeft.filter((p) => partiesRight.includes(p)).length;
        if (overlap > bestOverlap) {
          bestOverlap = overlap;
          best = rSeg;
        }
      });
      if (best && bestOverlap >= 1) {
        const x1 = left.x + 8;
        const x2 = right.x - 8;
        const cx1 = x1 + (x2 - x1) * 0.5;
        const cx2 = x1 + (x2 - x1) * 0.5;
        ribbons.push({
          d: `M${x1},${lSeg.y} C${cx1},${lSeg.y} ${cx2},${best.y} ${x2},${best.y} L${x2},${best.y + best.h} C${cx2},${best.y + best.h} ${cx1},${lSeg.y + lSeg.h} ${x1},${lSeg.y + lSeg.h} Z`,
          color: lSeg.color,
        });
      }
    });
  }

  return (
    <div className="lineage">
      <svg viewBox={`0 0 ${W} ${H}`} className="lineage__svg" role="img" aria-label="Coalition lineage across years">
        {ribbons.map((r, i) => (
          <path key={i} d={r.d} fill={r.color} opacity="0.18" />
        ))}
        {columns.map((col) => (
          <g key={col.year}>
            <text x={col.x} y={H - 4} textAnchor="middle" fontSize="10" fill="#555">{col.year}</text>
            {col.segments.map((s) => (
              <g key={s.name}>
                <rect
                  x={col.x - 6}
                  y={s.y}
                  width={12}
                  height={Math.max(2, s.h)}
                  fill={s.color}
                  stroke="#fff"
                  strokeWidth="0.5"
                />
                {s.h > 14 && (
                  <text
                    x={col.x}
                    y={s.y + s.h / 2 + 3}
                    textAnchor="middle"
                    fontSize="8"
                    fill="#fff"
                    style={{ pointerEvents: 'none' }}
                  >
                    {s.seats}
                  </text>
                )}
              </g>
            ))}
          </g>
        ))}
      </svg>
    </div>
  );
};

export default CoalitionLineage;
